import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { toast } from "sonner";
import { ArrowUpRight, Check, ChevronDown, UserPlus, X } from "lucide-react";
import { useScope } from "@/hooks/use-scope";
import {
  assignSignal,
  bulkResolveSignals,
  listAssignableUsers,
  listOperationalSignalsLifecycle,
  reopenSignal,
} from "@/lib/operational-signals.functions";
import {
  composeSituations,
  groupSituationsByLane,
  type SituationRecommendation,
  type SituationCard,
  type SituationLane,
  type SituationSignal,
} from "@/lib/operations/situation";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ShipmentIssuesOpsPanel } from "@/components/operations/ShipmentIssuesOpsPanel";
import { ActiveWorkPanel } from "@/components/operations/ActiveWorkPanel";
import { signalEntityHref } from "@/lib/signal-entity-href";

/**
 * Sprint 37 · M8 — Operations Workspace, time-horizon lanes.
 *
 * This route is the new "desk" experience, running side-by-side with the
 * legacy `/app/operations` surface until M11 flips it as the default.
 *
 * Design commitments:
 *
 * - **Lanes are kinds of managerial attention, not buckets.** Do Now is
 *   imperative; Next is a proposal; Today is planning; Watch is awareness.
 *   The voice register changes per lane while the visual grammar stays
 *   identical, so a manager reads the whole desk in one glance.
 * - **No scores are ever displayed.** Ordering within a lane uses
 *   `SituationCard._score` internally; nothing about that number surfaces.
 * - **No filters, no dropdowns, no drill-downs on this milestone.** The
 *   desk is what needs attention right now. M9 adds the inline drawer; M10
 *   adds the five affordances. This milestone is the room, not the tools.
 * - **Quiet when well-run.** Each lane can render an empty state, and
 *   empty lanes are a feature, not a failure.
 */
export const Route = createFileRoute("/_authenticated/app/operations/desk")({
  validateSearch: z.object({
    situation: z.string().optional(),
  }),
  component: OperationsDesk,
});

type SignalRow = SituationSignal & {
  effective_status?: SituationSignal["status"];
};

function OperationsDesk() {
  const { tenantId, facilityId } = useScope();
  const signalsFn = useServerFn(listOperationalSignalsLifecycle);
  const { situation: openSituationId } = Route.useSearch();
  const navigate = Route.useNavigate();

  const openSituation = useCallback(
    (id: string | null) => {
      navigate({
        search: (prev: { situation?: string }) => ({
          ...prev,
          situation: id ?? undefined,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const { data: signals = [], isLoading } = useQuery<SignalRow[]>({
    queryKey: ["ops-desk-signals", tenantId, facilityId],
    enabled: !!tenantId,
    refetchInterval: 15_000,
    queryFn: () =>
      signalsFn({
        data: {
          tenantId: tenantId!,
          facilityId: facilityId ?? undefined,
          sinceMinutes: 1440,
          limit: 200,
          status: "active",
        },
      }) as Promise<SignalRow[]>,
  });

  const situations = useMemo(() => {
    // Treat the lifecycle-adjusted `effective_status` as the source of truth
    // so a snooze that has already expired shows up as `open` on the desk.
    const normalized: SituationSignal[] = signals.map((s) => ({
      id: s.id,
      domain: s.domain,
      signal_type: s.signal_type,
      severity: s.severity,
      entity_type: s.entity_type,
      entity_id: s.entity_id,
      occurred_at: s.occurred_at,
      status: (s.effective_status ?? s.status) as SituationSignal["status"],
      payload: s.payload ?? null,
    }));
    const cards = composeSituations({ signals: normalized });
    return {
      byLane: groupSituationsByLane(cards),
      byId: new Map(cards.map((c) => [c.id, c])),
      all: cards,
    };
  }, [signals]);

  const selectedCard = openSituationId
    ? situations.byId.get(openSituationId) ?? null
    : null;

  // Signal lookup so the drawer can show the contributing rows inline
  // without a second round-trip. The desk already loaded them.
  const signalsById = useMemo(() => {
    const m = new Map<string, SignalRow>();
    for (const s of signals) m.set(s.id, s);
    return m;
  }, [signals]);

  const totalActive =
    situations.byLane["do-now"].length + situations.byLane.next.length;
  const wellRun = !isLoading && totalActive === 0
    && situations.byLane.today.length === 0
    && situations.byLane.watch.length === 0;

  return (
    <div className="mx-auto w-full max-w-[1600px] p-6 space-y-6">
      <header>
        <h1 className="text-xl font-semibold tracking-tight">Operations Workspace</h1>
        <p className="text-sm text-muted-foreground mt-1">
          What requires your attention. Not a dashboard.
        </p>
      </header>

      {!isLoading && !wellRun && (
        <HealthStrip signals={signals} cards={situations.all} />
      )}

      <ShipmentIssuesOpsPanel />

      <ActiveWorkPanel />

      {wellRun ? (
        <CalmDesk />
      ) : (
        <div
          className={
            "flex gap-6 " +
            (selectedCard ? "lg:flex-row flex-col" : "flex-col")
          }
        >
          <div
            className={
              "min-w-0 flex-1 grid gap-4 " +
              (selectedCard
                ? "lg:grid-cols-2 md:grid-cols-2"
                : "lg:grid-cols-4 md:grid-cols-2")
            }
          >
            <Lane
              lane="do-now"
              title="Do Now"
              register="Immediate intervention"
              emptyLine="Nothing needs immediate intervention."
              cards={situations.byLane["do-now"]}
              selectedId={openSituationId}
              onSelect={openSituation}
            />
            <Lane
              lane="next"
              title="Next"
              register="Likely problem unless you act soon"
              emptyLine="Nothing to sequence in the next 30–90 minutes."
              cards={situations.byLane.next}
              selectedId={openSituationId}
              onSelect={openSituation}
            />
            <Lane
              lane="today"
              title="Today"
              register="Shift-shaping decisions"
              emptyLine="No shift-shaping decisions pending."
              cards={situations.byLane.today}
              selectedId={openSituationId}
              onSelect={openSituation}
            />
            <Lane
              lane="watch"
              title="Watch"
              register="Awareness only"
              emptyLine="Nothing worth watching right now."
              cards={situations.byLane.watch}
              selectedId={openSituationId}
              onSelect={openSituation}
            />
          </div>

          {/* Inline drawer on lg+ — reflows the lanes rather than covering
              them. On smaller screens we fall back to a right sheet so the
              manager still gets the same content without losing the desk
              behind a modal. */}
          {selectedCard && (
            <>
              <aside className="hidden lg:block w-[420px] shrink-0">
                <div className="sticky top-6">
                  <SituationDrawerBody
                    card={selectedCard}
                    signalsById={signalsById}
                    tenantId={tenantId ?? null}
                    onClose={() => openSituation(null)}
                  />
                </div>
              </aside>
              <Sheet
                open={!!selectedCard}
                onOpenChange={(o) => !o && openSituation(null)}
              >
                <SheetContent
                  side="right"
                  className="lg:hidden w-[92vw] sm:max-w-md p-0"
                >
                  <SituationDrawerBody
                    card={selectedCard}
                    signalsById={signalsById}
                    tenantId={tenantId ?? null}
                    onClose={() => openSituation(null)}
                    embedded
                  />
                </SheetContent>
              </Sheet>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------
// Lane column
// -----------------------------------------------------------------------

function Lane({
  lane,
  title,
  register,
  emptyLine,
  cards,
  selectedId,
  onSelect,
}: {
  lane: SituationLane;
  title: string;
  register: string;
  emptyLine: string;
  cards: SituationCard[];
  selectedId?: string;
  onSelect: (id: string) => void;
}) {
  return (
    <section className="flex flex-col">
      <header className="mb-3 flex items-baseline justify-between">
        <div>
          <h2 className="text-sm font-semibold tracking-tight">{title}</h2>
          <p className="text-[11px] text-muted-foreground mt-0.5">{register}</p>
        </div>
        {cards.length > 0 && (
          <span className="text-[11px] text-muted-foreground tabular-nums">
            {cards.length}
          </span>
        )}
      </header>

      {cards.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border/50 px-4 py-6 text-[12px] text-muted-foreground/70">
          {emptyLine}
        </div>
      ) : (
        <ol className="space-y-2">
          {cards.map((card) => (
            <li key={card.id}>
              <SituationCardView
                card={card}
                lane={lane}
                selected={selectedId === card.id}
                onSelect={() => onSelect(card.id)}
              />
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

// -----------------------------------------------------------------------
// Situation card
//
// The card grammar (verb-first headline · chips · why now) is identical
// across lanes. Only the *voice register* — the composed leading phrase
// and, in Next, the "if we do nothing" tail — differs. That way a manager
// reads the whole desk with one visual language.
// -----------------------------------------------------------------------

function SituationCardView({
  card,
  lane,
  selected,
  onSelect,
}: {
  card: SituationCard;
  lane: SituationLane;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={
        "group w-full text-left rounded-lg border bg-background/60 p-3 transition-colors " +
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 " +
        (selected
          ? "border-foreground/60 bg-foreground/[0.03] shadow-[0_0_0_1px_hsl(var(--foreground)/0.15)]"
          : lane === "do-now"
            ? "border-destructive/40 hover:border-destructive/60"
            : lane === "next"
              ? "border-foreground/20 hover:border-foreground/40"
              : lane === "today"
                ? "border-border hover:border-foreground/30"
                : "border-border/60 hover:border-border")
      }
    >
      <div className="flex items-start gap-2">
        <SeverityDot lane={lane} />
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-medium leading-snug text-foreground">
            {card.headline}
          </div>
          <div className="mt-1 text-[11px] text-muted-foreground">
            <span className="font-medium text-foreground/80">{card.subject.label}</span>
            <span className="mx-1.5 text-border">·</span>
            <span>{card.whyNow}</span>
          </div>

          {lane === "next" && card.costOfInaction && (
            <div className="mt-1.5 text-[11px] text-muted-foreground/80 italic">
              {card.costOfInaction}
            </div>
          )}

          {card.chips.length > 1 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {card.chips.slice(0, 5).map((chip, i) => (
                <span
                  key={i}
                  className="rounded border border-border/60 px-1.5 py-0.5 text-[10px] text-muted-foreground"
                >
                  {chip}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

function SeverityDot({ lane }: { lane: SituationLane }) {
  // If a card is on this desk, it's worth addressing — we don't grade
  // importance with color. The dot exists only to anchor the row and,
  // for Do Now, provide a motion cue.
  const tone =
    lane === "do-now"
      ? "bg-destructive"
      : lane === "next"
        ? "bg-foreground/60"
        : "bg-muted-foreground/50";
  const opacity = lane === "watch" ? "opacity-50" : "opacity-90";
  const pulse = lane === "do-now" ? "animate-pulse" : "";
  return (
    <span
      aria-hidden
      className={`mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full ${tone} ${opacity} ${pulse}`}
    />
  );
}

// -----------------------------------------------------------------------
// Calm state
// -----------------------------------------------------------------------

function CalmDesk() {
  return (
    <div className="rounded-xl border border-dashed border-border/50 px-6 py-16 text-center">
      <div className="text-sm text-foreground">Nothing requires your attention.</div>
      <div className="mt-1 text-[12px] text-muted-foreground">
        The floor is running its normal rhythm.
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// Inline drawer body
//
// The drawer's job is to *reveal more of the same story*, not to open a
// second application. It follows the card's grammar — verdict → subject
// → why now → proposed action → contributing signals — and stops there.
// Deeper investigation (the underlying order, wave, dock, or signal
// history) belongs one deep-link away in its own workspace; the drawer
// only offers the escape hatch, never re-implements the destination.
//
// Lifecycle actions (accept, override, delegate) are M10. This milestone
// deliberately does not add controls — it earns the manager's trust that
// expanding a card explains the situation before it asks them to act.
//
// M10 update — The Decision Panel now sits inside the drawer. It carries
// the five affordances (Understand · Modify · Delegate · Accept · Override).
// The rest of the drawer is unchanged: the grammar is what earned trust,
// so we do not disturb it. Actions accompany the story; they don't replace
// it. Success language talks about *decisions*, not system events —
// "Decision recorded", not "Signals resolved" — because the operator is
// authoritative and CairnOS is simply carrying out their decision.
// -----------------------------------------------------------------------

function SituationDrawerBody({
  card,
  signalsById,
  tenantId,
  onClose,
  embedded = false,
}: {
  card: SituationCard;
  signalsById: Map<string, SignalRow>;
  tenantId: string | null;
  onClose: () => void;
  embedded?: boolean;
}) {
  const contributing = card.contributingSignalIds
    .map((id) => signalsById.get(id))
    .filter((s): s is SignalRow => !!s);

  // Composition-first, ratification later: if the upstream signal did not
  // carry a recommendation, compose a minimal one from the card's own
  // grammar so the manager always has *something* to accept or override.
  // The proposal text is the card's verb-first headline — nothing invented.
  const recommendation: SituationRecommendation | null =
    card.recommendation ?? synthesizeRecommendation(card);

  return (
    <div
      className={
        "flex h-full flex-col " +
        (embedded
          ? "p-5"
          : "rounded-xl border border-border bg-background/80 backdrop-blur-sm p-5")
      }
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground/70">
            {laneLabel(card.lane)}
          </div>
          <h2 className="mt-1 text-[15px] font-semibold leading-snug text-foreground">
            {card.headline}
          </h2>
          <div className="mt-1 text-[12px] text-muted-foreground">
            <span className="font-medium text-foreground/80">
              {card.subject.label}
            </span>
            <span className="mx-1.5 text-border">·</span>
            <span className="capitalize">{card.subject.kind.replace("-", " ")}</span>
          </div>
          <SituationTimeline contributing={contributing} />
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close situation"
          className="rounded p-1 text-muted-foreground/70 transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-5 space-y-5 overflow-y-auto pr-1">
        {/* Why now — the one-sentence answer the manager was going to ask. */}
        <Section title="Why this is on the desk">
          <p className="text-[12px] leading-relaxed text-foreground/85">
            {card.whyNow}
          </p>
        </Section>

        {recommendation && card.lane !== "watch" && (
          <DecisionPanel
            card={card}
            recommendation={recommendation}
            tenantId={tenantId}
            onDecisionRecorded={onClose}
          />
        )}

        {/* Contributing signals — the atoms behind the composed situation.
            The desk is composition-first: the manager should see the truths
            that produced the sentence, not just the sentence. */}
        <Section title={`Contributing signals · ${contributing.length}`}>
          <ul className="space-y-1.5">
            {contributing.map((s) => {
              const href = signalEntityHref(s.entity_type, s.entity_id);
              const body = (
                <>
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="text-[12px] font-medium text-foreground/90">
                      {humanize(s.signal_type)}
                    </div>
                    {href && (
                      <ArrowUpRight className="h-3 w-3 text-muted-foreground/60 group-hover:text-foreground" />
                    )}
                  </div>
                  <div className="mt-0.5 text-[11px] text-muted-foreground">
                    {formatRelative(s.occurred_at)}
                    <span className="mx-1.5 text-border">·</span>
                    <span className="capitalize">
                      {(s.effective_status ?? s.status)}
                    </span>
                  </div>
                </>
              );
              return (
                <li key={s.id}>
                  {href ? (
                    <a
                      href={href}
                      className="group block rounded-md border border-border/60 bg-background/40 px-2.5 py-2 transition-colors hover:border-foreground/40 hover:bg-background/70"
                    >
                      {body}
                    </a>
                  ) : (
                    <div className="rounded-md border border-border/60 bg-background/40 px-2.5 py-2">
                      {body}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </Section>

      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/80">
        {title}
      </h3>
      <div className="mt-2">{children}</div>
    </section>
  );
}

function laneLabel(lane: SituationLane): string {
  switch (lane) {
    case "do-now":
      return "Do Now";
    case "next":
      return "Next";
    case "today":
      return "Today";
    case "watch":
      return "Watch";
  }
}

function humanize(s: string): string {
  return s.replace(/[_.]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatRelative(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "";
  const deltaMin = Math.max(0, Math.round((Date.now() - t) / 60_000));
  if (deltaMin < 1) return "just now";
  if (deltaMin < 60) return `${deltaMin}m ago`;
  const h = Math.floor(deltaMin / 60);
  const m = deltaMin % 60;
  if (h < 24) return m === 0 ? `${h}h ago` : `${h}h ${m}m ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

// -----------------------------------------------------------------------
// M10 — The Decision Panel
//
// Five affordances live here — Understand, Modify, Delegate, Accept,
// Override. The panel is deliberately restrained:
//
//   • Understand is inline (the rationale bullets), not a separate mode.
//   • Modify edits the proposal in place. The edited text is what the
//     manager accepts — the system carries out the manager's decision,
//     not the algorithm's original text.
//   • Delegate assigns the underlying signals to another person. It does
//     not resolve them. Delegation is *"I'm not the right decider"* —
//     never *"the situation is over."*
//   • Accept resolves the contributing signals with the accepted
//     proposal as the resolution note (audited as such).
//   • Override resolves them with the manager's reason as the note.
//     The signal lifecycle is the audit trail; no new emitter, no new
//     table.
//
// Success language is decision-centric ("Decision recorded"), never
// system-centric ("Signals resolved"). Motion is calm: a subtle fade,
// then the drawer closes. The Undo toast reopens the affected signals
// through the existing lifecycle path.
// -----------------------------------------------------------------------

/**
 * Compose a minimal recommendation from the card's own grammar when the
 * upstream signal did not carry one. This is composition — proposal text
 * derives from the already-composed headline — not invention.
 */
function synthesizeRecommendation(card: SituationCard): SituationRecommendation | null {
  if (card.lane === "watch") return null;
  return {
    id: `synthesized:${card.id}`,
    proposal: card.headline,
    expectedOutcome: card.expectedOutcome ?? null,
    primary: true,
  };
}

function DecisionPanel({
  card,
  recommendation,
  tenantId,
  onDecisionRecorded,
}: {
  card: SituationCard;
  recommendation: SituationRecommendation;
  tenantId: string | null;
  onDecisionRecorded: () => void;
}) {
  const qc = useQueryClient();
  // Accept and Override are two decisions with the same effect on the
  // signal lifecycle (resolve, note captures the manager's call). One fn.
  const resolveFn = useServerFn(bulkResolveSignals);
  const assignFn = useServerFn(assignSignal);
  const reopenFn = useServerFn(reopenSignal);

  const [proposal, setProposal] = useState(recommendation.proposal);
  const [editing, setEditing] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  // Keep the editable proposal in sync when the manager switches cards
  // without closing the drawer.
  useEffect(() => {
    setProposal(recommendation.proposal);
    setEditing(false);
    setRejecting(false);
    setRejectReason("");
  }, [card.id, recommendation.proposal]);

  const modified = proposal.trim() !== recommendation.proposal.trim();
  const ids = card.contributingSignalIds;

  const invalidateDesk = () => {
    qc.invalidateQueries({ queryKey: ["ops-desk-signals"] });
  };

  // Undo republishes the signals through the same lifecycle path the
  // rest of the workspace uses. No bespoke undo history.
  const undo = async (label: string) => {
    try {
      await Promise.all(ids.map((id) => reopenFn({ data: { signalId: id, trigger: "manual", reason: `Undo: ${label}` } })));
      invalidateDesk();
      toast.success("Decision reversed", {
        description: "The situation is back on the desk.",
      });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Undo failed";
      toast.error(msg);
    }
  };

  const accept = useMutation({
    mutationFn: async () => {
      const note = modified
        ? `Accepted (modified): ${proposal.trim()}`
        : `Accepted: ${proposal.trim()}`;
      return resolveFn({ data: { signalIds: ids, note } });
    },
    onSuccess: () => {
      invalidateDesk();
      toast.success("Decision recorded", {
        description: modified
          ? "Your modified proposal was accepted."
          : "Your acceptance was recorded.",
        action: { label: "Undo", onClick: () => undo("accept") },
        duration: 6000,
      });
      onDecisionRecorded();
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : "Could not record decision";
      toast.error(msg);
    },
  });

  const reject = useMutation({
    mutationFn: async () => {
      const reason = rejectReason.trim();
      const note = reason ? `Overrode: ${reason}` : "Overrode without reason";
      return resolveFn({ data: { signalIds: ids, note } });
    },
    onSuccess: () => {
      invalidateDesk();
      toast.success("Decision recorded", {
        description: "You overrode the proposal.",
        action: { label: "Undo", onClick: () => undo("override") },
        duration: 6000,
      });
      onDecisionRecorded();
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : "Could not record decision";
      toast.error(msg);
    },
  });

  const delegate = useMutation({
    mutationFn: async (assigneeId: string) => {
      await Promise.all(ids.map((id) => assignFn({ data: { signalId: id, assigneeId } })));
    },
    onSuccess: () => {
      invalidateDesk();
      toast.success("Delegated", {
        description: "The right person now owns this decision.",
        duration: 4000,
      });
      onDecisionRecorded();
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : "Could not delegate";
      toast.error(msg);
    },
  });

  const busy =
    accept.isPending || reject.isPending || delegate.isPending;

  return (
    <section className="rounded-lg border border-foreground/15 bg-foreground/[0.02] p-3 animate-fade-in">
      <div className="flex items-center justify-between">
        <h3 className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/80">
          Proposed action
        </h3>
        {!editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-[10px] text-muted-foreground/80 hover:text-foreground underline-offset-2 hover:underline"
          >
            Modify
          </button>
        )}
      </div>

      {/* Proposal — editable in place. What the manager accepts is what
          the manager sees; CairnOS carries it out, not the other way. */}
      {editing ? (
        <Textarea
          value={proposal}
          onChange={(e) => setProposal(e.target.value)}
          onBlur={() => setEditing(false)}
          autoFocus
          rows={2}
          className="mt-2 text-[13px] leading-snug"
          maxLength={500}
        />
      ) : (
        <p className="mt-2 text-[13px] leading-snug text-foreground">
          {proposal}
          {modified && (
            <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground/70">
              modified
            </span>
          )}
        </p>
      )}

      {/* Understand — the four questions, answered inline. Never a
          separate mode; the manager should not have to click to understand
          what they are about to decide. */}
      <dl className="mt-3 space-y-1 text-[11px] leading-relaxed">
        <RationaleRow
          label="Because"
          value={card.whyNow}
        />
        <RationaleRow
          label="If accepted"
          value={card.expectedOutcome ?? defaultExpected(card)}
        />
        <RationaleRow
          label="If ignored"
          value={card.costOfInaction ?? defaultCost(card)}
          muted
        />
      </dl>

      {/* Reject reason — appears inline when the manager chooses to
          override, so the two-step decision doesn't feel like a modal. */}
      {rejecting && (
        <div className="mt-3 rounded-md border border-border/60 bg-background/60 p-2 animate-fade-in">
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground/80">
            Why are you overriding?
          </label>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Optional — helps the next manager"
            rows={2}
            className="mt-1 text-[12px] leading-snug"
            maxLength={500}
            autoFocus
          />
          <div className="mt-2 flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 text-[11px]"
              onClick={() => {
                setRejecting(false);
                setRejectReason("");
              }}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 text-[11px]"
              onClick={() => reject.mutate()}
              disabled={busy}
            >
              Confirm override
            </Button>
          </div>
        </div>
      )}

      {/* Actions — Accept is the primary decision; Override and Delegate
          sit alongside as equally legitimate choices. Never framed as
          "the system executing" — this is the manager making a call. */}
      {!rejecting && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            className="h-8 text-[12px] gap-1.5"
            onClick={() => accept.mutate()}
            disabled={busy || proposal.trim().length === 0}
          >
            <Check className="h-3.5 w-3.5" />
            Accept
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 text-[12px] text-muted-foreground hover:text-foreground"
            onClick={() => setRejecting(true)}
            disabled={busy}
          >
            Override
          </Button>
          <DelegateControl
            tenantId={tenantId}
            onSelect={(id) => delegate.mutate(id)}
            disabled={busy}
          />
        </div>
      )}
    </section>
  );
}

function RationaleRow({
  label,
  value,
  muted = false,
}: {
  label: string;
  value: string;
  muted?: boolean;
}) {
  return (
    <div className="flex gap-2">
      <dt className="w-[70px] shrink-0 text-[10px] uppercase tracking-wider text-muted-foreground/70">
        {label}
      </dt>
      <dd
        className={
          "min-w-0 flex-1 " +
          (muted ? "text-muted-foreground/85 italic" : "text-foreground/85")
        }
      >
        {value}
      </dd>
    </div>
  );
}

/** Composed fallback so the "If accepted" row is always answered. */
function defaultExpected(card: SituationCard): string {
  return `The situation on ${card.subject.label} is resolved and the contributing signals are closed.`;
}

/**
 * Composed fallback so the "If ignored" row is always answered.
 * Lane-based, not severity-based — a card already on Do Now shouldn't be
 * told it "will likely become a Do Now situation shortly."
 */
function defaultCost(card: SituationCard): string {
  switch (card.lane) {
    case "do-now":
      return "Pressure keeps building. Every minute compounds the recovery cost.";
    case "next":
      return "This slides into Do Now if you don't act in the next 30–90 minutes.";
    case "today":
      return "Stable but drifting. You'll still be addressing it — just later, with fewer options.";
    case "watch":
    default:
      return "No immediate consequence, but the signal stays on the desk.";
  }
}

function DelegateControl({
  tenantId,
  onSelect,
  disabled,
}: {
  tenantId: string | null;
  onSelect: (id: string) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const usersFn = useServerFn(listAssignableUsers);

  const { data: users = [] } = useQuery<
    Array<{ id: string; display_name: string | null }>
  >({
    queryKey: ["ops-desk-assignees", tenantId, search],
    enabled: !!tenantId && open,
    queryFn: () =>
      usersFn({ data: { tenantId: tenantId!, search: search || undefined } }),
    staleTime: 30_000,
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 text-[12px] text-muted-foreground hover:text-foreground gap-1.5"
          disabled={disabled || !tenantId}
        >
          <UserPlus className="h-3.5 w-3.5" />
          Delegate
          <ChevronDown className="h-3 w-3 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Find a person…"
          className="w-full rounded-md border border-border bg-background px-2 py-1 text-[12px] focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          autoFocus
        />
        <div className="mt-2 max-h-56 overflow-y-auto">
          {users.length === 0 ? (
            <div className="px-2 py-3 text-center text-[11px] text-muted-foreground/80">
              No one to delegate to yet.
            </div>
          ) : (
            <ul className="space-y-0.5">
              {users.map((u) => (
                <li key={u.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onSelect(u.id);
                      setOpen(false);
                    }}
                    className="w-full rounded-md px-2 py-1.5 text-left text-[12px] hover:bg-muted focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    {u.display_name ?? "Unnamed teammate"}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
// -----------------------------------------------------------------------
// M11 — Health strip
//
// A thin, decision-support register of the six operational areas. The
// Scoreboard tells the *story* of the warehouse in broadcast voice; the
// health strip tells the *state* of each area in a manager's voice.
// Same underlying truth, different register — the strip is composed
// entirely from the signals already loaded for the desk, so no new
// server function, no new schema, no new emitter.
//
// The strip answers one question, quickly, from across the desk:
// *"Which parts of the operation should I be watching?"* — before the
// manager reads a single situation card. It is a filter for their
// attention, not a summary of activity.
//
// Motion discipline carries over from the Scoreboard: only *urgent*
// area dots pulse. Watch is calm. OK is barely there.
// -----------------------------------------------------------------------

type AreaTone = "ok" | "watch" | "urgent";
type AreaKey =
  | "Receiving"
  | "Picking"
  | "Packing"
  | "Shipping"
  | "Returns"
  | "Inventory";

const AREA_ORDER: AreaKey[] = [
  "Receiving",
  "Picking",
  "Packing",
  "Shipping",
  "Returns",
  "Inventory",
];

/**
 * Map a signal domain to a health-strip area. The strip is composition
 * over what we already have — this table is the only place the mapping
 * lives, and tuning it is a UI change.
 */
function areaForDomain(domain: string): AreaKey | null {
  const d = domain.toLowerCase();
  if (d.includes("receiv")) return "Receiving";
  if (d.includes("putaway")) return "Receiving";
  if (d.includes("pick")) return "Picking";
  if (d.includes("pack")) return "Packing";
  if (d.includes("ship")) return "Shipping";
  if (d.includes("carrier") || d.includes("dock")) return "Shipping";
  if (d.includes("return") || d.includes("rma")) return "Returns";
  if (d.includes("inventor") || d.includes("cycle") || d.includes("stock"))
    return "Inventory";
  return null;
}

interface AreaState {
  area: AreaKey;
  tone: AreaTone;
  count: number;
  verdict: string;
}

function composeAreaStates(
  signals: SignalRow[],
  cards: SituationCard[],
): AreaState[] {
  // We compose from *situations*, not raw signals, so the strip stays in
  // step with what the manager sees on the desk. A signal that clusters
  // into a situation is one unit of pressure — not N units — matching
  // the composition-first principle.
  const cardArea = new Map<string, AreaKey | null>();
  for (const c of cards) {
    // Find the dominant area across the card's contributing signals.
    // Ties break by AREA_ORDER position (Receiving first).
    const votes = new Map<AreaKey, number>();
    for (const sid of c.contributingSignalIds) {
      const s = signals.find((r) => r.id === sid);
      if (!s) continue;
      const a = areaForDomain(s.domain);
      if (!a) continue;
      votes.set(a, (votes.get(a) ?? 0) + 1);
    }
    let best: AreaKey | null = null;
    let bestCount = 0;
    for (const a of AREA_ORDER) {
      const v = votes.get(a) ?? 0;
      if (v > bestCount) {
        best = a;
        bestCount = v;
      }
    }
    cardArea.set(c.id, best);
  }

  const tones: Record<AreaKey, AreaTone> = {
    Receiving: "ok",
    Picking: "ok",
    Packing: "ok",
    Shipping: "ok",
    Returns: "ok",
    Inventory: "ok",
  };
  const counts: Record<AreaKey, number> = {
    Receiving: 0,
    Picking: 0,
    Packing: 0,
    Shipping: 0,
    Returns: 0,
    Inventory: 0,
  };

  for (const c of cards) {
    const a = cardArea.get(c.id);
    if (!a) continue;
    counts[a]++;
    // Worst-severity wins; only Do Now / error-level situations light an
    // area urgent. Warn goes to watch. Info stays ok — informational
    // situations should not paint an area red.
    if (c.severity === "critical" || c.severity === "error") {
      tones[a] = "urgent";
    } else if (c.severity === "warn" && tones[a] !== "urgent") {
      tones[a] = "watch";
    }
  }

  return AREA_ORDER.map((area) => ({
    area,
    tone: tones[area],
    count: counts[area],
    verdict: verdictForArea(tones[area], counts[area]),
  }));
}

function verdictForArea(tone: AreaTone, count: number): string {
  if (count === 0) return "Clear";
  if (tone === "urgent") return "Under pressure";
  if (tone === "watch") return "Elevated";
  return "Steady";
}

function HealthStrip({
  signals,
  cards,
}: {
  signals: SignalRow[];
  cards: SituationCard[];
}) {
  const states = useMemo(
    () => composeAreaStates(signals, cards),
    [signals, cards],
  );

  return (
    <section
      aria-label="Operational area health"
      className="rounded-lg border border-border/60 bg-background/40 px-3 py-2"
    >
      <div className="grid grid-cols-3 gap-x-4 gap-y-2 sm:grid-cols-6">
        {states.map((s) => (
          <div key={s.area} className="flex items-center gap-2 min-w-0">
            <AreaDot tone={s.tone} />
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-1.5">
                <span className="text-[11px] font-medium tracking-tight text-foreground truncate">
                  {s.area}
                </span>
                {s.count > 0 && (
                  <span className="text-[10px] tabular-nums text-muted-foreground/80">
                    {s.count}
                  </span>
                )}
              </div>
              <div
                className={
                  "text-[10px] truncate " +
                  (s.tone === "urgent"
                    ? "text-destructive/90"
                    : s.tone === "watch"
                      ? "text-amber-600 dark:text-amber-400"
                      : "text-muted-foreground/70")
                }
              >
                {s.verdict}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function AreaDot({ tone }: { tone: AreaTone }) {
  const color =
    tone === "urgent"
      ? "bg-destructive"
      : tone === "watch"
        ? "bg-amber-500"
        : "bg-muted-foreground/40";
  const pulse = tone === "urgent" ? "animate-pulse" : "";
  const opacity = tone === "ok" ? "opacity-60" : "opacity-90";
  return (
    <span
      aria-hidden
      className={`inline-block h-1.5 w-1.5 shrink-0 rounded-full ${color} ${opacity} ${pulse}`}
    />
  );
}

// -----------------------------------------------------------------------
// M11 — Situation timeline (drawer header)
//
// The lightest possible "when did this start?" context. Composed from
// the earliest and latest `occurred_at` across contributing signals.
// One line, no widgets, no chart. It exists so a manager landing on a
// drawer instantly knows whether they're looking at a fresh problem or
// something that has been building.
// -----------------------------------------------------------------------

function SituationTimeline({ contributing }: { contributing: SignalRow[] }) {
  if (contributing.length === 0) return null;
  const times = contributing
    .map((s) => new Date(s.occurred_at).getTime())
    .filter((t) => Number.isFinite(t));
  if (times.length === 0) return null;
  const oldest = Math.min(...times);
  const newest = Math.max(...times);
  const started = formatRelative(new Date(oldest).toISOString());
  const latest = formatRelative(new Date(newest).toISOString());
  const same = oldest === newest;
  return (
    <div className="mt-1.5 text-[11px] text-muted-foreground/80">
      Started {started}
      {!same && (
        <>
          <span className="mx-1.5 text-border">·</span>
          Newest signal {latest}
        </>
      )}
    </div>
  );
}
